import type { PointerEvent as ReactPointerEvent, RefObject } from 'react'
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { computeCollageLayout } from '../../lib/collageLayout'
import type { CollagePhoto } from '../../lib/collageTypes'
import CollageTile, { type PointerHandlers } from './CollageTile'

/**
 * The preview renders at half size so the whole collage fits on screen without the browser's own
 * zoom — a collage with few photos can otherwise need a very tall row to fill the available width
 * (see `computeCollageLayout`), pushing the bottom of the collage off-screen. Export is unaffected:
 * it derives its resolution from `measureRef`'s untransformed width (see `PhotoCollage.tsx`), not
 * from anything CSS-scaled here.
 */
const PREVIEW_SCALE = 0.5

export type CollageGridProps = {
  photos: CollagePhoto[]
  gapPx: number
  backgroundColor: string
  onReorder: (fromIndex: number, toIndex: number) => void
  onRemove: (photoId: string) => void
  containerRef: RefObject<HTMLDivElement | null>
  registerTileEl: (photoId: string, el: HTMLDivElement | null) => void
  registerImgEl: (photoId: string, el: HTMLImageElement | null) => void
}

export default function CollageGrid({
  photos,
  gapPx,
  backgroundColor,
  onReorder,
  onRemove,
  containerRef,
  registerTileEl,
  registerImgEl,
}: CollageGridProps) {
  // Measured from a plain, unscaled sibling rather than containerRef's own element, since that
  // element gets an explicit width below (to stay full-size for layout and export while only its
  // paint shrinks) — measuring it directly here would be circular.
  const measureRef = useRef<HTMLDivElement>(null)
  const [fullWidth, setFullWidth] = useState(0)
  const [dragPhotoId, setDragPhotoId] = useState<string | null>(null)
  const [hoverPhotoId, setHoverPhotoId] = useState<string | null>(null)

  useLayoutEffect(() => {
    const el = measureRef.current
    if (!el) return
    const compute = () => setFullWidth(el.clientWidth)
    compute()
    const ro = new ResizeObserver(compute)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const photosById = useMemo(() => new Map(photos.map((p) => [p.id, p])), [photos])

  const layout = useMemo(
    () =>
      computeCollageLayout(
        photos.map((p) => {
          const aspectRatio = p.naturalWidth / p.naturalHeight
          // A photo with a zero or non-finite dimension (a malformed file that still fired `load`)
          // would otherwise feed Infinity/NaN into collageLayout's running aspect-ratio sums,
          // corrupting every row's height, not just this one photo's tile. Fall back to square.
          return { id: p.id, aspectRatio: Number.isFinite(aspectRatio) && aspectRatio > 0 ? aspectRatio : 1 }
        }),
        fullWidth,
        gapPx,
      ),
    [photos, fullWidth, gapPx],
  )

  const fullContentHeight =
    layout.rows.reduce((sum, row) => sum + row.rowHeightPx, 0) +
    Math.max(0, layout.rows.length - 1) * layout.verticalGapPx +
    2 * gapPx

  const handleDragPointerDown = useCallback((e: ReactPointerEvent<HTMLElement>, photoId: string) => {
    e.preventDefault()
    e.currentTarget.setPointerCapture(e.pointerId)
    setDragPhotoId(photoId)
    setHoverPhotoId(photoId)
  }, [])

  const handleDragPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      if (!dragPhotoId) return
      const el = document.elementFromPoint(e.clientX, e.clientY)
      const tileEl = el instanceof Element ? el.closest<HTMLElement>('[data-tile-id]') : null
      setHoverPhotoId(tileEl?.dataset.tileId ?? null)
    },
    [dragPhotoId],
  )

  const endDrag = useCallback(() => {
    if (dragPhotoId && hoverPhotoId && dragPhotoId !== hoverPhotoId) {
      const fromIndex = photos.findIndex((p) => p.id === dragPhotoId)
      const toIndex = photos.findIndex((p) => p.id === hoverPhotoId)
      if (fromIndex !== -1 && toIndex !== -1) onReorder(fromIndex, toIndex)
    }
    setDragPhotoId(null)
    setHoverPhotoId(null)
  }, [dragPhotoId, hoverPhotoId, photos, onReorder])

  const makeDragHandlers = (photoId: string): PointerHandlers => ({
    onPointerDown: (e) => handleDragPointerDown(e, photoId),
    onPointerMove: handleDragPointerMove,
    onPointerUp: endDrag,
    onPointerCancel: endDrag,
  })

  return (
    <>
      <div ref={measureRef} style={{ width: '100%', height: 0 }} aria-hidden />
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <div
          style={{
            width: fullWidth * PREVIEW_SCALE,
            height: fullContentHeight * PREVIEW_SCALE,
            overflow: 'hidden',
          }}
        >
          <div
            ref={containerRef}
            data-testid="collage-grid"
            style={{
              width: `${fullWidth}px`,
              transform: `scale(${PREVIEW_SCALE})`,
              transformOrigin: 'top left',
              display: 'flex',
              flexDirection: 'column',
              gap: `${layout.verticalGapPx}px`,
              // Vertical-only: the left/right border is already built into each row's own edge
              // slots (see collageLayout.ts), so padding here too would double it.
              padding: `${gapPx}px 0`,
              backgroundColor,
            }}
          >
            {layout.rows.map((row, rowIndex) => (
              // Keyed by position, not by row.photoIds.join(',') — the grouping is re-solved from
              // scratch on every change, so a content-derived key would change (forcing every tile
              // in the row to remount and re-decode) whenever the *set* of photos sharing a row
              // shifts, even for photos that didn't move. Keying by position instead lets React
              // reconcile each tile by its own key (see CollageTile below) and reuse the ones that
              // are still in this row, only mounting/unmounting the ones that actually changed.
              <div key={rowIndex} style={{ display: 'flex', gap: `${row.gapPx}px`, justifyContent: 'center' }}>
                {row.photoIds.map((id) => {
                  const photo = photosById.get(id)
                  if (!photo) return null
                  return (
                    <CollageTile
                      key={id}
                      photo={photo}
                      heightPx={row.rowHeightPx}
                      isDragSource={dragPhotoId === id}
                      isDropTarget={hoverPhotoId === id && dragPhotoId !== null && dragPhotoId !== id}
                      dragHandleHandlers={makeDragHandlers(id)}
                      onRemove={onRemove}
                      registerTileEl={registerTileEl}
                      registerImgEl={registerImgEl}
                    />
                  )
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  )
}
